import { Banner, BannerActions, BannerContent, BannerIcon, BannerText, BannerTitle, Button, Popover, Typography } from '@cobliteam/ui/components';
import Icon, { type IconNames } from '@cobliteam/ui/icons';
import type { ReactNode } from 'react';

export type PageWarningSeverity = 'warning' | 'info';

export interface PageWarningItem {
  id: string;
  severity: PageWarningSeverity;
  title?: ReactNode;
  description: ReactNode;
  action?: {
    label: ReactNode;
    onClick: () => void;
  };
  onClose?: () => void;
}

export interface PageWarningProps {
  items: PageWarningItem[];
  title?: ReactNode;
  ariaLabel?: string;
}

const ICON_BY_SEVERITY: Record<PageWarningSeverity, IconNames> = {
  warning: 'triangle-alert',
  info: 'info',
};

const COLOR_BY_SEVERITY: Record<PageWarningSeverity, 'warning' | 'primary'> = {
  warning: 'warning',
  info: 'primary',
};

const PageWarning = ({ items, title = 'Avisos', ariaLabel = 'Avisos' }: PageWarningProps) => {
  if (items.length === 0) return null;

  const hasWarning = items.some((item) => item.severity === 'warning');
  const triggerColor = hasWarning ? 'warning' : 'primary';
  const triggerIcon: IconNames = hasWarning ? 'triangle-alert' : 'info';
  const badgeColor = hasWarning ? 'bg-danger' : 'bg-primary';

  return (
    <Popover>
      <div className='relative inline-flex'>
        <Popover.Trigger asChild>
          <Button type='button' variant='surface' color={triggerColor} size='sm' icon={<Icon name={triggerIcon} />} aria-label={ariaLabel} data-testid='page-warning-trigger' />
        </Popover.Trigger>
        <div className={`-top-1.5 -right-1.5 pointer-events-none absolute flex size-5 items-center justify-center rounded-full font-medium text-white text-xs ${badgeColor}`}>
          {items.length}
        </div>
      </div>
      <Popover.Content align='start' side='bottom' className='drawer-md flex max-h-[75vh] max-w-[90vw] flex-col gap-3 overflow-auto border-gray-6 shadow-xl'>
        <Typography variant='text-lg-semibold'>{title}</Typography>
        <div className='flex flex-col gap-2'>
          {items.map((item) => (
            <Banner key={item.id} color={COLOR_BY_SEVERITY[item.severity]} onClose={item.onClose}>
              <BannerIcon icon={<Icon name={ICON_BY_SEVERITY[item.severity]} color={item.severity === 'warning' ? 'warning' : 'primary'} />} />
              <BannerContent>
                {item.title && <BannerTitle>{item.title}</BannerTitle>}
                <BannerText>{item.description}</BannerText>
              </BannerContent>
              {item.action && (
                <BannerActions>
                  <Button type='button' variant='ghost' color={COLOR_BY_SEVERITY[item.severity]} onClick={item.action.onClick}>
                    {item.action.label}
                  </Button>
                </BannerActions>
              )}
            </Banner>
          ))}
        </div>
      </Popover.Content>
    </Popover>
  );
};

export default PageWarning;
